import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { ToastService } from './toast.service';
import { TranslationService } from './translation.service';

export interface CommentAuthor {
  id: string;
  username: string;
  firstName?: string;
  lastName?: string;
  avatar?: string;
}

export interface Comment {
  id: string;
  content: string;
  entityType: 'post' | 'point' | 'material' | 'article' | 'product';
  entityId: string;
  parentId?: string;
  likeCount: number;
  replyCount: number;
  isEdited: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  author: CommentAuthor;
  replies?: string[];
  isLikedByCurrentUser?: boolean;
}

export interface CreateCommentDto {
  content?: string;
  entityType: 'post' | 'point' | 'material' | 'article' | 'product';
  entityId: string;
  parentId?: string;
  /** Report types - when present, creates a 'report' type comment */
  reportTypes?: string[];
}

export interface UpdateCommentDto {
  content?: string;
  entityType?: 'post' | 'point' | 'material' | 'article' | 'product';
  entityId?: string;
  parentId?: string;
}

export interface CommentFilters {
  entityType: string;
  entityId: string;
  /** Filter by comment type: 'comment' | 'report' */
  type?: string;
  page?: number;
  limit?: number;
}

export interface PaginatedCommentsResponse {
  data: Comment[];
  meta: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class CommentService {
  private readonly http = inject(HttpClient);
  private readonly toast = inject(ToastService);
  private readonly t = inject(TranslationService);
  private readonly apiUrl = `${environment.apiUrl}/comments`;

  /**
   * Create a comment
   * Creates a comment on any entity (post, point, material, etc.)
   * Requires authentication.
   */
  create(dto: CreateCommentDto): Observable<Comment> {
    return this.http.post<Comment>(this.apiUrl, dto).pipe(
      catchError(error => {
        this.toast.presentErrorFromResponse(error, this.t.instant('commentService.sendError'));
        return throwError(() => error);
      })
    );
  }

  /**
   * Get comments for an entity
   * Retrieves comments for a specific entity with pagination.
   */
  findByEntity(filters: CommentFilters): Observable<PaginatedCommentsResponse> {
    let params = new HttpParams()
      .set('entityType', filters.entityType)
      .set('entityId', filters.entityId);
    
    if (filters.type) params = params.set('type', filters.type);
    if (filters.page) params = params.set('page', filters.page.toString());
    if (filters.limit) params = params.set('limit', filters.limit.toString());

    return this.http.get<PaginatedCommentsResponse>(this.apiUrl, { params }).pipe(
      catchError(error => {
        this.toast.presentErrorFromResponse(error, this.t.instant('commentService.loadError'));
        return throwError(() => error);
      })
    );
  }

  /**
   * Get replies for a comment
   * Retrieves all replies to a specific comment.
   */
  getReplies(commentId: string): Observable<Comment[]> {
    return this.http.get<Comment[]>(`${this.apiUrl}/${commentId}/replies`);
  }

  /**
   * Update a comment
   * Updates a comment. Only the author can update their comments.
   * Requires authentication.
   */
  update(id: string, dto: UpdateCommentDto): Observable<Comment> {
    return this.http.patch<Comment>(`${this.apiUrl}/${id}`, dto).pipe(
      catchError(error => {
        this.toast.presentErrorFromResponse(error, this.t.instant('commentService.updateError'));
        return throwError(() => error);
      })
    );
  }

  /**
   * Delete a comment
   * Soft deletes a comment. Only the author can delete their comments.
   * Requires authentication.
   */
  remove(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`).pipe(
      catchError(error => {
        this.toast.presentErrorFromResponse(error, this.t.instant('commentService.deleteError'));
        return throwError(() => error);
      })
    );
  }
}
